// ProctoringEngine — top-level orchestrator for browser-side cheating
// detection (Layer 3 MediaPipe + future sub-layers).
//
// Mounts into the coding test (iCode.jsx), reads the candidate's webcam
// stream, and runs every enabled detector against it. Each detector emits
// events via a shared event emitter (batched POST to /api/cheating/event),
// and the most recent event surfaces as a real-time warning overlay so the
// candidate can self-correct.
//
// Props:
//   stream           : MediaStream  (already opened webcam stream from iCode)
//   testId           : string       (URL ?test_id)
//   applicationId    : number?      (optional, for dashboard joins)
//   candidateEmail   : string?      (optional, for dashboard display)
//   active           : boolean      (false = pause detection but keep mounted)
//
// Lifecycle:
//   * On mount: load feature flags → check MediaPipe availability → start
//     enabled detectors.
//   * On unmount: stop all detectors, flush event buffer, remove video el.
//   * On `active=false`: detectors pause (e.g. test submitted).
//
// Failure handling:
//   * MediaPipe CDN unreachable → log once, don't crash, gracefully degrade
//     (browser-only detectors still work, candidates still take the test).
//   * No webcam stream → emit one INFO event, render nothing, don't crash.
//   * Detector init failure → that one detector skipped; others run.

import React, { useEffect, useRef, useState } from 'react';
import { createEventEmitter } from './eventEmitter';
import { checkMediaPipeAvailable } from './mediapipeLoader';
import { createFaceDetector } from './detectors/faceDetector';
import { createHandDetector } from './detectors/handDetector';
import { createAudioDetector } from './detectors/audioDetector';
// Layer 4 cross-engine bridge — fires a window event whenever a detector
// emits a non-info finding. The SnapshotEngine listens for this and grabs
// a server-side webcam frame as tamper-proof evidence of the moment.
import { fireCheatingDetectorEvent } from '../Snapshots/SnapshotEngine';
import {
  useCheatingFeatureFlags,
  isCheatingFeatureEnabled,
  getCheatingFeatureThreshold,
} from './useCheatingFeatureFlag';
import ProctoringWarning from './ProctoringWarning';

// Feature keys grouped by the detector module that owns them. Each group
// shares ONE MediaPipe model instance — adding a new sub-feature to an
// existing detector (e.g. eye_gaze added to faceDetector) doesn't need a
// new entry here. New DETECTOR types (hand, audio) get their own group.
const FACE_DETECTOR_KEYS = [
  'mediapipe.face_presence',
  'mediapipe.multiple_faces',
  'mediapipe.head_pose',
  'mediapipe.eye_gaze',
];
const HAND_DETECTOR_KEYS = ['mediapipe.hand_tracking'];
const AUDIO_DETECTOR_KEYS = ['mediapipe.audio_classifier'];

export default function ProctoringEngine({
  stream,
  testId,
  applicationId = null,
  candidateEmail = null,
  active = true,
}) {
  const { flags, loading: flagsLoading } = useCheatingFeatureFlags();
  const videoRef = useRef(null);
  const emitterRef = useRef(null);
  const detectorsRef = useRef([]);
  const [currentWarning, setCurrentWarning] = useState(null);
  // Tracks why MediaPipe didn't initialise so we can log once and surface
  // diagnostics if needed.
  const [_mediaPipeStatus, setMediaPipeStatus] = useState({ checked: false });

  // ── Bind the supplied MediaStream to the offscreen video element ────────
  // We use our OWN video element (not the user-visible one in iCode) so
  // we can pause/resume detection without disturbing what the candidate
  // sees. It stays display:none — only used as a detection source.
  useEffect(() => {
    if (!stream || !videoRef.current) return undefined;
    try {
      videoRef.current.srcObject = stream;
      videoRef.current.muted = true; // detector doesn't need audio playback
      videoRef.current.playsInline = true;
      const playPromise = videoRef.current.play();
      // Some browsers reject autoplay without a user gesture — webcam
      // streams are usually exempt but we silence the warning anyway.
      if (playPromise && typeof playPromise.catch === 'function') {
        playPromise.catch(() => { /* autoplay blocked, will retry on resume */ });
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('[proctoring] failed to bind stream:', err?.message || err);
    }
    return () => {
      try {
        if (videoRef.current) videoRef.current.srcObject = null;
      } catch (_) { /* defensive */ }
    };
  }, [stream]);

  // ── Build the event emitter once per (testId, applicationId) ────────────
  useEffect(() => {
    if (!testId) return undefined;
    emitterRef.current = createEventEmitter({
      testId,
      applicationId,
      candidateEmail,
    });
    return () => {
      try {
        void emitterRef.current?.stop();
      } catch (_) { /* defensive */ }
      emitterRef.current = null;
    };
  }, [testId, applicationId, candidateEmail]);

  // ── Wire the emitter so detector events also surface as in-app warnings ──
  // We wrap the real emitter so a single function does THREE things:
  //   1. POST the event to the backend (via the buffered emitter)
  //   2. Surface a warning toast for non-info events
  //   3. Fire the Layer-4 snapshot bus so SnapshotEngine grabs a frame
  //      of the exact moment (tamper-proof evidence for the dashboard).
  const emitWithWarning = (event) => {
    if (!emitterRef.current) return;
    emitterRef.current.emit(event);
    if (event.severity && event.severity !== 'info') {
      setCurrentWarning(event);
      fireCheatingDetectorEvent(event.feature_key, event.severity);
    }
  };

  // ── Boot the detectors when prerequisites are ready ─────────────────────
  useEffect(() => {
    if (!active) return undefined;
    if (flagsLoading) return undefined;
    if (!videoRef.current || !stream) return undefined;
    if (!emitterRef.current) return undefined;

    // For each detector group, check if any of its sub-features are enabled.
    // We only spin up a detector when at least one of its sub-features is
    // on — saves the CPU/network cost of loading a model that wouldn't
    // contribute anything.
    const anyFaceEnabled = FACE_DETECTOR_KEYS.some((k) => Boolean(flags?.[k]?.enabled));
    const anyHandEnabled = HAND_DETECTOR_KEYS.some((k) => Boolean(flags?.[k]?.enabled));
    const anyAudioEnabled = AUDIO_DETECTOR_KEYS.some((k) => Boolean(flags?.[k]?.enabled));

    if (!anyFaceEnabled && !anyHandEnabled && !anyAudioEnabled) {
      return undefined;
    }

    // One-shot availability check so we log a clear reason if CDN/firewall
    // blocks MediaPipe. Doesn't block boot — if it fails, individual
    // detector start() calls reject and we silently skip them.
    let cancelled = false;
    void (async () => {
      const status = await checkMediaPipeAvailable();
      if (cancelled) return;
      setMediaPipeStatus({ checked: true, ...status });
      if (!status.available) {
        // eslint-disable-next-line no-console
        console.warn(
          '[proctoring] MediaPipe unavailable — face/hand/audio detectors will be skipped. Cause:',
          status.error
        );
        emitWithWarning({
          feature_key: 'mediapipe.face_presence',
          severity: 'info',
          score_delta: 0,
          message: 'Proctoring AI failed to load',
          evidence: { reason: 'mediapipe_unavailable', error: status.error },
        });
        return;
      }

      const startedDetectors = [];

      // ── Face detector (presence + multiple + head pose + eye gaze) ──
      if (anyFaceEnabled) {
        try {
          const faceController = createFaceDetector({
            videoElement: videoRef.current,
            emit: emitWithWarning,
            getFlag: isCheatingFeatureEnabled,
            getThreshold: getCheatingFeatureThreshold,
          });
          await faceController.start();
          if (cancelled) {
            faceController.stop();
          } else {
            detectorsRef.current.push(faceController);
            startedDetectors.push('face');
          }
        } catch (err) {
          // eslint-disable-next-line no-console
          console.warn('[proctoring] face detector init failed:', err?.message || err);
        }
      }

      // ── Hand detector (hands-off + extra-hand patterns) ──
      if (anyHandEnabled && !cancelled) {
        try {
          const handController = createHandDetector({
            videoElement: videoRef.current,
            emit: emitWithWarning,
            getFlag: isCheatingFeatureEnabled,
            getThreshold: getCheatingFeatureThreshold,
          });
          await handController.start();
          if (cancelled) {
            handController.stop();
          } else {
            detectorsRef.current.push(handController);
            startedDetectors.push('hand');
          }
        } catch (err) {
          // eslint-disable-next-line no-console
          console.warn('[proctoring] hand detector init failed:', err?.message || err);
        }
      }

      // ── Audio detector (sustained speech / second voice) ──
      // Needs the candidate's MediaStream (not the video element) because
      // it routes through AudioContext. Gracefully no-ops if the stream
      // doesn't have an audio track.
      if (anyAudioEnabled && !cancelled && stream) {
        try {
          const audioController = createAudioDetector({
            stream,
            emit: emitWithWarning,
            getFlag: isCheatingFeatureEnabled,
            getThreshold: getCheatingFeatureThreshold,
          });
          await audioController.start();
          if (cancelled) {
            audioController.stop();
          } else {
            detectorsRef.current.push(audioController);
            startedDetectors.push('audio');
          }
        } catch (err) {
          // eslint-disable-next-line no-console
          console.warn('[proctoring] audio detector init failed:', err?.message || err);
        }
      }

      if (!cancelled && startedDetectors.length > 0) {
        emitWithWarning({
          feature_key: 'mediapipe.face_presence',
          severity: 'info',
          score_delta: 0,
          message: 'Proctoring active',
          evidence: { detectors_started: startedDetectors },
        });
      }
    })();

    return () => {
      cancelled = true;
      // Stop every detector we managed to start. Each stop() is idempotent.
      while (detectorsRef.current.length > 0) {
        const d = detectorsRef.current.pop();
        try { d.stop(); } catch (_) { /* defensive */ }
      }
    };
    // We intentionally only re-run when `active` flips or any sub-feature
    // flag toggles. Adding the flag values directly to the deps array
    // keeps it small and explicit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    active,
    flagsLoading,
    stream,
    flags?.['mediapipe.face_presence']?.enabled,
    flags?.['mediapipe.multiple_faces']?.enabled,
    flags?.['mediapipe.head_pose']?.enabled,
    flags?.['mediapipe.eye_gaze']?.enabled,
    flags?.['mediapipe.hand_tracking']?.enabled,
    flags?.['mediapipe.audio_classifier']?.enabled,
  ]);

  return (
    <>
      {/* Offscreen video element used as the detection source.
          display:none is fine — getUserMedia keeps the stream alive
          regardless of layout visibility. */}
      <video
        ref={videoRef}
        autoPlay
        muted
        playsInline
        style={{ display: 'none' }}
        aria-hidden="true"
      />
      <ProctoringWarning
        warning={currentWarning}
        onDismiss={() => setCurrentWarning(null)}
      />
    </>
  );
}
